import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader, AlertCircle, Users } from 'lucide-react';
import { fetchProceedingDigest, ProceedingDigest, DigestIssue } from '../lib/digestService';

const STANCE_COLORS: Record<string, string> = {
  support: '#059669', oppose: '#dc2626', modify: '#d97706', mixed: '#7c3aed', other: '#64748b',
};

/**
 * Per-topic drill-down: every filer who engaged one issue in a proceeding, with
 * their stance and page-cited position. Reached by clicking a topic on the digest
 * page (/proceedings/:docketNumber/digest/:topicId). Reuses the cached digest —
 * no separate API call — and picks the issue by id.
 */
export function ProceedingTopicPage() {
  const { docketNumber, topicId } = useParams<{ docketNumber: string; topicId: string }>();
  const [digest, setDigest] = useState<ProceedingDigest | null>(null);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'absent' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setPhase('loading'); setError(null);
    fetchProceedingDigest(docketNumber || '')
      .then((d) => {
        if (!alive) return;
        if (d && d.status === 'ok') { setDigest(d); setPhase('ready'); }
        else setPhase('absent');
      })
      .catch((e) => { if (alive) { setError(e?.message || 'Failed to load'); setPhase('error'); } });
    return () => { alive = false; };
  }, [docketNumber]);

  const issue: DigestIssue | undefined = digest?.issues.find((i) => i.id === topicId);
  const positions = issue?.all_positions?.length ? issue.all_positions : issue?.positions || [];

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-gradient-to-br from-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-5 py-8">
          <Link to={`/proceedings/${docketNumber}/digest`}
            className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to {docketNumber} digest
          </Link>
          {issue ? (
            <>
              <div className="text-blue-300 text-xs font-semibold uppercase tracking-wide">
                Docket {docketNumber} · Topic
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mt-1">{issue.title}</h1>
              {issue.proposition && (
                <p className="text-slate-300 text-sm mt-2 max-w-2xl">
                  <span className="font-semibold text-slate-200">Filers split on: </span>
                  “{issue.proposition}”
                </p>
              )}
              <div className="mt-2 text-slate-300 text-sm inline-flex items-center gap-1.5">
                <Users className="w-4 h-4" /> {issue.n_filers} filers · {issue.n_positions} positions
              </div>
            </>
          ) : (
            <h1 className="text-2xl md:text-3xl font-bold mt-1">{docketNumber} — Topic</h1>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5">
        {phase === 'loading' && (
          <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
            <Loader className="w-5 h-5 animate-spin" /> Loading…
          </div>
        )}
        {phase === 'error' && (
          <div className="flex items-center gap-2 text-red-600 py-16 justify-center">
            <AlertCircle className="w-5 h-5" /> {error}
          </div>
        )}
        {(phase === 'absent' || (phase === 'ready' && !issue)) && (
          <div className="text-slate-500 py-16 text-center">
            That topic isn’t available.{' '}
            <Link to={`/proceedings/${docketNumber}/digest`} className="text-blue-600 hover:underline">
              Back to the digest
            </Link>.
          </div>
        )}

        {phase === 'ready' && issue && (
          <>
            <div className="mb-3 mt-5 flex flex-wrap gap-1.5">
              {(['support', 'oppose', 'modify', 'mixed'] as const).map((b) =>
                issue.stance?.[b] ? (
                  <span key={b} className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
                    style={{ background: `${STANCE_COLORS[b]}1a`, color: STANCE_COLORS[b] }}>
                    {issue.stance[b]} {b}
                  </span>
                ) : null
              )}
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Every filer who engaged this issue. Each position is grounded in the cited page of the
              source filing — verify against the source before relying on it.
            </p>
            <ul className="list-none p-0">
              {positions.map((p, i) => {
                const pages = p.pages?.length ? p.pages : p.page != null ? [p.page] : [];
                return (
                  <li key={i} className="py-2.5 border-t border-slate-200 text-[15px]">
                    <span className="font-semibold">{p.filer}</span>
                    {p.stance && p.stance !== 'other' && (
                      <span className="ml-1.5 align-middle text-[11px] font-semibold px-1.5 py-0.5 rounded-full"
                        style={{
                          background: `${STANCE_COLORS[p.stance] || STANCE_COLORS.other}1a`,
                          color: STANCE_COLORS[p.stance] || STANCE_COLORS.other,
                        }}>
                        {p.stance}
                      </span>
                    )}
                    <span className="text-slate-400"> — </span>
                    {p.summary}
                    {pages.length > 0 && (
                      <span className="text-blue-600 text-xs font-semibold ml-1 whitespace-nowrap">
                        {pages.length > 1 ? 'pp.' : 'p.'} {pages.join(', ')}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="py-10">
              <Link to={`/proceedings/${docketNumber}/digest`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:underline">
                <ArrowLeft className="w-4 h-4" /> Back to all topics in {docketNumber}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ProceedingTopicPage;
